import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Checkbox, TableContainer } from '@mui/material';
import { DocumentSortType, IDocument, SortOrder } from '../../../../@types/Document';
import DocumentsTableItem from './Item';
import { Text } from '../../../UI/Typography';
import { useEffect, useState } from 'react';
import { Theme } from '../../../../theme';
import { isEmpty } from 'lodash';
import { MeliorTranslate } from '../../../MeliorTranslate';
import Sorter from '../../../Sorter';

interface IDocumentsTableProps {
    documents: IDocument[];
    selectedDocuments: IDocument[];
    selectedTags: string[];
    sortOrder: SortOrder;
    sortBy: DocumentSortType;
    refetch: () => void;
    setSelectedDocuments: (docs: IDocument[]) => void;
    removeDocuments: (docs: IDocument[]) => void;
    setIsAddTagOpen: (isOpen: boolean) => void;
    setTaggingMode: (mode: string) => void;
    setDocumentTags: (tags: object) => void;
    setTaggingDocIds: (ids: string[]) => void;
    setSelectedTags: (tags: string[]) => void;
    setSelectedType: (type: string) => void;
    onDocumentReady: (doc: IDocument) => void;
    setIsDynamicInsightsOpen: (isOpen: boolean) => void;
    setDocumentsForDynInsights: (doc: IDocument[]) => void;
    setSortBy: (sortBy: DocumentSortType) => void;
    setSortOrder: (sortOrder: SortOrder) => void;
}

const DocumentsTable = ({
    documents,
    selectedDocuments,
    selectedTags,
    sortOrder,
    sortBy,
    refetch,
    setSelectedDocuments,
    removeDocuments,
    setIsAddTagOpen,
    setTaggingMode,
    setDocumentTags,
    setTaggingDocIds,
    setSelectedTags,
    setSelectedType,
    onDocumentReady,
    setIsDynamicInsightsOpen,
    setDocumentsForDynInsights,
    setSortBy,
    setSortOrder,
}: IDocumentsTableProps) => {
    const [isSelectingAll, setIsSelectingAll] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [isIntermediate, setIsIntermediate] = useState<boolean>(false);

    const selectAllChanged = () => {
        const selectingAll = !isSelectingAll;
        setIsSelectingAll(selectingAll);
        const filteredSelected = selectedDocuments.filter((doc) => !documents.includes(doc));

        if (selectingAll) {
            setSelectedDocuments(filteredSelected.concat(documents));
        } else {
            setSelectedDocuments(filteredSelected);
        }
    };

    useEffect(() => {
        if (!selectedDocuments) {
            setIsIntermediate(false);
            return;
        }
        const selectedCount = documents.filter((doc) => selectedDocuments.includes(doc)).length;
        setIsIntermediate(selectedCount > 0 && selectedCount != documents.length);
    }, [selectedDocuments]);

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell style={{ width: '10px' }}>
                            <Checkbox
                                onClick={() => {
                                    selectAllChanged();
                                }}
                                indeterminate={isIntermediate}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                style={{
                                    color:
                                        (isHovered || isSelectingAll) &&
                                        !(
                                            documents.length != selectedDocuments.length &&
                                            !isEmpty(selectedDocuments)
                                        )
                                            ? Theme.primary
                                            : 'gray',
                                    padding: 0,
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            <Sorter
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                displayName="File Name"
                                sortName={DocumentSortType.FILE_NAME}
                                setSortBy={setSortBy}
                                setSortOrder={setSortOrder}
                            />
                        </TableCell>
                        <TableCell>
                            <Sorter
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                displayName="Document Type"
                                sortName={DocumentSortType.TYPE}
                                setSortBy={setSortBy}
                                setSortOrder={setSortOrder}
                            />
                        </TableCell>
                        <TableCell>
                            <Text customFontWeight={700}>
                                <MeliorTranslate valueKey="Tags" />
                            </Text>
                        </TableCell>
                        <TableCell sx={{ width: '90px' }}>
                            <Sorter
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                displayName="Status"
                                sortName={DocumentSortType.STATUS}
                                setSortBy={setSortBy}
                                setSortOrder={setSortOrder}
                            />
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {documents.map((document: IDocument, index) => (
                        <DocumentsTableItem
                            documents={documents}
                            key={`${document.id}-${index}`}
                            document={document}
                            isSelectingAll={isSelectingAll}
                            selectedDocuments={selectedDocuments}
                            selectedTags={selectedTags}
                            onDeleteSuccess={removeDocuments}
                            onReUploadSuccess={refetch}
                            setSelectedDocuments={setSelectedDocuments}
                            setIsAddTagOpen={setIsAddTagOpen}
                            setTaggingMode={setTaggingMode}
                            setDocumentTags={setDocumentTags}
                            setTaggingDocIds={setTaggingDocIds}
                            setSelectedTags={setSelectedTags}
                            setSelectedType={setSelectedType}
                            onDocumentReady={(document) => onDocumentReady(document)}
                            setIsDynamicInsightsOpen={setIsDynamicInsightsOpen}
                            setDocumentsForDynInsights={setDocumentsForDynInsights}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DocumentsTable;
